<template>
  <RemoteAutocomplete
    :id="`search-term-${searchId}`"
    ref="remoteRef"
    class="lh--force-white-background"
    :model-value="selectedValue"
    :label="label + (required ? '*' : '')"
    :remote-search="searchMedications"
    :allow-free-text="false"
    :disabled="disabled"
    :component-bind="{
      noFilter: true,
      clearable: true,
      placeholder: 'Start typing medication (at least 3 characters) or enter full NDC.',
      hideNoData: !showNoResultsMessage,
      menu: shouldShowMenu,
    }"
    @update:menu="(open) => (menuOpen = open)"
    @update:model-value="handleSelection"
    @keyup.enter="handleEnterKey"
    @keyup.esc="cleanInput">
    <template #item="{ props: itemProps, item }">
      <v-list-item v-bind="itemProps">
        <template #title>
          {{ item.title }}
          <span v-if="item.raw.payload.nationalDrugCode"> [{{ item.raw.payload.nationalDrugCode }}] </span>
        </template>
      </v-list-item>
    </template>
    <template #no-data>
      <v-list-item v-if="showNoResultsMessage">
        <v-list-item-title class="text-caption text-medium-emphasis">
          No medication found. Enter full medication name and press Enter to continue.
        </v-list-item-title>
      </v-list-item>
    </template>
  </RemoteAutocomplete>
</template>

<script setup>
import { ref, computed } from "vue";
import { mainServices } from "@/services/mainServices";
import { normalizeNdc } from "@/utils";
import RemoteAutocomplete from "@/components/RemoteAutocomplete.vue";

const props = defineProps({
  data: {
    type: Object,
    required: false,
    default: () => ({
      label: "Please type at least 3 characters to search for medication or enter full NDC.",
      searchId: "medication-search",
    }),
  },
  required: {
    type: Boolean,
    required: false,
  },
  allowFreeTextInput: {
    type: Boolean,
    default() {
      return false;
    },
  },
  disabled: {
    type: Boolean,
    default() {
      return false;
    },
  },
});

const emit = defineEmits(["item-selected"]);

const remoteRef = ref(null);
const selectedValue = ref(null);

const label = computed(
  () => props.data?.label || "Please type at least 3 characters to search for medication or enter full NDC."
);
const searchId = computed(() => props.data?.searchId || "medication-search");

// loadingRef flips on synchronously when a search starts, so "not loading" means the last search completed
const showNoResultsMessage = computed(() => {
  const remote = remoteRef.value?.remote;
  return (
    props.allowFreeTextInput &&
    !!remote &&
    !remote.loadingRef &&
    remote.itemsRef.length === 0 &&
    (remote.searchRef ?? "").trim().length >= 3
  );
});

// Vuetify opens the menu on every keystroke; only let it show once there is something to list
const menuOpen = ref(false);
const shouldShowMenu = computed(
  () => menuOpen.value && ((remoteRef.value?.remote.itemsRef.length ?? 0) > 0 || showNoResultsMessage.value)
);

// Item value is unique per row (an NDC can repeat across rows); the fields the parent needs travel in `payload`
const transformMedicationData = (medication, index) => ({
  title: medication.medication || "",
  value: `${index}:${medication.nationalDrugCode || medication.medication}`,
  payload: {
    medication: medication.medication,
    nationalDrugCode: medication.nationalDrugCode || null,
    packageSizeUnit: medication.packageSizeUnit || null,
    name: medication.name || null,
  },
});

const searchMedications = (query) => {
  const trimmedQuery = (query || "").trim();
  // An NDC pasted from a source system normally carries dashes; search on the
  // normalized 11-digit form so it resolves the same as a dashless paste.
  const normalizedNdc = normalizeNdc(trimmedQuery);
  const isNdc = /^\d{11}$/.test(normalizedNdc);
  const isName = trimmedQuery.length >= 3;
  if (!isNdc && !isName) {
    return Promise.resolve([]);
  }
  return mainServices
    .searchMedications(isNdc ? normalizedNdc : trimmedQuery)
    .then((results) => results.map(transformMedicationData))
    .catch(() => []);
};

function handleSelection(value) {
  if (!value) {
    selectedValue.value = null;
    return;
  }
  const item = remoteRef.value?.remote.itemsRef.find((i) => i.value === value);
  if (item) {
    selectedValue.value = value;
    emit("item-selected", { title: item.title, value: item.payload });
  }
}

function handleEnterKey(event) {
  if (props.allowFreeTextInput && event.target.value) {
    const freeTextItem = {
      label: event.target.value,
      value: { medication: event.target.value },
    };
    emit("item-selected", freeTextItem);
  }
}

function cleanInput() {
  selectedValue.value = null;
  remoteRef.value?.remote.reset("");
}
</script>

<style lang="scss" scoped>
@import "../styles/pages/_patient-information-form.scss";
@import "../styles/pages/_insurance-navigator-page";
</style>
